import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";  
import { SignInResponse } from "../models/response/signInResponse";
import { SignInRequest } from "../models/request/signInRequest";
import { authService } from "../services/authService";
import { State } from "../models/state/state";

const storedToken = localStorage.getItem("token"); 
const storedUserPermissions = localStorage.getItem("userPermissions");

const initialState: State = {
  token: storedToken || null,
  status: "idle",
  isLoading: false,
  error: null,
  message: "",
  data: [], 
  userPermissions: storedUserPermissions || null,
};

export const signIn = createAsyncThunk<
  SignInResponse,
  SignInRequest,
  { rejectValue: string }
>("auth/signIn", async (credentials, { rejectWithValue }) => {
  try {
    const response = await authService.signIn(credentials);
    if (response.success && response.token) {
      localStorage.setItem('token', response.token);
      return response;
    } else {
      return response;
    }
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const forgotPassword = createAsyncThunk<
  any,
  {email: string},
  { rejectValue: string }
>("auth/forgotPassword", async ({email}, { rejectWithValue }) => {
  try {
    const response = await authService.forgotPassword(email); 
    return response; 
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const changePassword = createAsyncThunk<
  any,
  {id:string, password: string},
  { rejectValue: string }
>("auth/changePassword", async ({id, password}, { rejectWithValue }) => {
  try {
    const response = await authService.changePassword(id, password); 
    return response; 
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});


export const signInInvestor = createAsyncThunk<
  SignInResponse,
  SignInRequest,
  { rejectValue: string }
>("auth/signInInvestor", async (credentials, { rejectWithValue }) => {
  try {
    const response = await authService.signInInvestor(credentials); 
    return response; 
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const verifyCode = createAsyncThunk<
  any,
  {code: string, email:string},
  { rejectValue: string }
>("auth/verifyCode", async ({code, email}, { rejectWithValue }) => {
  try {
    const response = await authService.verifyCode(code, email); 
    return response.data; 
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const logout = createAsyncThunk("auth/logout", async () => {
  await authService.logout();
});

const authReducer = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = "loading";
        state.isLoading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.status = "idle";
        state.token = action.payload.token ?? null;  
        state.userPermissions = JSON.stringify(action.payload.permissions ?? "{}");
        state.isLoading = false;
        if (state.token) {
          localStorage.setItem("token", state.token);
        }
        if (state.userPermissions) {
          localStorage.setItem("userPermissions", state.userPermissions);
        }
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = "failed";
        state.isLoading = false;
        if (action.payload) {
          state.error = action.payload;
          state.message = action.payload;
        } else {
          state.message = "An unexpected error occurred";
        }
      })
      .addCase(signInInvestor.pending, (state) => {
        state.status = "loading";
        state.isLoading = true;
      })
      .addCase(signInInvestor.fulfilled, (state, action) => {
        state.status = "idle";
        state.token = action.payload.token ?? null; 
        state.message = "Sign in successful"; 
        state.userPermissions = "{}";
        state.isLoading = false;
        if (state.token) {
          localStorage.setItem("token", state.token);
        }
        if (state.userPermissions) {
          localStorage.setItem("userPermissions", state.userPermissions);
        }
        state.isLoading = false; 
      })
      .addCase(signInInvestor.rejected, (state, action) => {
        state.status = "failed";
        state.isLoading = false;
        if (action.payload) {
          state.error = action.payload;
          state.message = action.payload;
        } else {
          state.message = "An unexpected error occurred";
        }
      })
      .addCase(forgotPassword.pending, (state) => {
        state.status = "loading";
        state.isLoading = true;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.status = "idle";
        state.token = action.payload.token ?? null; 
        state.message = "Sign in successful"; 
        state.isLoading = false; 
        state.userPermissions = "{}";
        state.isLoading = false;
        if (state.token) {
          localStorage.setItem("token", state.token);
        }
        if (state.userPermissions) {
          localStorage.setItem("userPermissions", state.userPermissions);
        }
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = "failed";
        state.isLoading = false;
        if (action.payload) {
          state.error = action.payload;
          state.message = action.payload;
        } else {
          state.message = "An unexpected error occurred";
        }
      })
      .addCase(changePassword.pending, (state) => {
        state.status = "loading";
        state.isLoading = true;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.status = "idle";
        state.token = action.payload.token ?? null; 
        state.message = "Sign in successful"; 
        state.isLoading = false; 
        state.userPermissions = "{}";
        state.isLoading = false;
        if (state.token) {
          localStorage.setItem("token", state.token);
        }
        if (state.userPermissions) {
          localStorage.setItem("userPermissions", state.userPermissions);
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.status = "failed";
        state.isLoading = false;
        if (action.payload) {
          state.error = action.payload;
          state.message = action.payload;
        } else {
          state.message = "An unexpected error occurred";
        }
      })
      .addCase(verifyCode.pending, (state) => {
        state.status = "loading";
        state.isLoading = true;
      })
      .addCase(verifyCode.fulfilled, (state, action) => {
        state.status = "idle";
        state.token = action.payload.token ?? null; 
        state.message = "Sign in successful"; 
        state.isLoading = false; 
        state.userPermissions = "{}";
        state.isLoading = false;
        if (state.token) {
          localStorage.setItem("token", state.token);
        }
        if (state.userPermissions) {
          localStorage.setItem("userPermissions", state.userPermissions);
        }
      })
      .addCase(verifyCode.rejected, (state, action) => {
        state.status = "failed";
        state.isLoading = false;
        if (action.payload) {
          state.error = action.payload;
          state.message = action.payload;
        } else {
          state.message = "An unexpected error occurred";
        }
      })
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.isLoading = false;
      });
  },
});

export default authReducer.reducer;
